/**
 * F16.B: title-bar chrome for a portal route. Replaces the default Win95
 * top frame inside `/c/${slug}` subtrees. The bar carries:
 *   - The firm's logo (or monogram fallback)
 *   - The firm's displayName
 *   - A "What is this?" affordance for the audience-explanations rule
 *     (Q&A #2 — "things must be explained")
 *   - A Sign-in button slot (wired in F16.C alongside the per-portal MSAL
 *     instance; for F16.B it renders as a placeholder with data-testid).
 *
 * Theme variables (--portal-bg, --portal-fg, --portal-accent, …) are
 * introduced in F16.B+. Until then the chrome reads `primaryColor` from
 * the branding row directly via a CSS custom property, keeping the
 * inline-style surface to dynamic values only (memory: `feedback_use_tailwind`).
 */

import { useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTenantBranding } from '../../lib/tenant/TenantBrandingContext';
import { useOptionalPortalMsal } from '../../lib/tenant/MsalInstanceProvider';
import { TenantLogo } from './TenantLogo';
import { ArtDecoOrnament } from './themes/ArtDecoOrnament';
import { testIds } from '../../lib/testIds';

export function TenantChrome({ children }: { children: ReactNode }) {
  const { branding, slug } = useTenantBranding();
  const navigate = useNavigate();
  const msal = useOptionalPortalMsal();
  const [explainOpen, setExplainOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  const account = msal?.account ?? null;
  const accountReady = msal?.ready ?? false;
  const accountLabel = account?.username ?? account?.name ?? null;

  async function handleSignIn() {
    if (!msal) {return;}
    setBusy(true);
    setSignInError(null);
    try {
      await msal.signIn();
    } catch (err) {
      // F16.I: surface a friendly explanation for the common
      // misconfiguration error (fake or stale tenantId in the seeded
      // tenantBranding row). The raw MSAL error includes
      // "AADSTS90002: Tenant '<guid>' not found" — useless to the
      // lawyer audience and looks like the product is broken.
      const raw = (err as Error).message || 'Sign-in failed';
      const friendly = friendlySignInError(raw, branding.lockedTenantId);
      setSignInError(friendly);
    } finally {
      setBusy(false);
    }
  }

  function friendlySignInError(raw: string, tenantId: string): string {
    if (raw.includes('AADSTS90002')) {
      return (
        `This portal is configured for a Microsoft 365 tenant that ` +
        `Microsoft can’t resolve (tenant ${tenantId}). ` +
        `Ask your operator to re-configure /c/${slug} with a valid tenant ID.`
      );
    }
    if (raw.includes('AADSTS50020')) {
      return (
        `Your Microsoft account isn’t a member of this firm’s tenant ` +
        `(${tenantId}). Sign in with a ${branding.displayName} account, ` +
        `or ask your operator to invite you as a guest.`
      );
    }
    if (raw.includes('AADSTS65001')) {
      return (
        `Your firm’s administrator needs to grant Repancy permission ` +
        `to read SharePoint. Forward this URL to IT: ` +
        `https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=…`
      );
    }
    if (raw.includes('popup_window_error') || raw.includes('window.open')) {
      return (
        `The sign-in popup was blocked. Allow popups for this site and ` +
        `click Sign in again.`
      );
    }
    if (raw.includes('interaction_in_progress')) {
      // F16.J: MSAL holds a sessionStorage lock when a prior popup
      // never finished. We try to clear it automatically inside
      // MsalInstanceProvider.signIn, but if MSAL races us to it
      // (or the cache is set on a different storage key prefix), tell
      // the user to refresh.
      return (
        `An earlier sign-in attempt is still being tracked by Microsoft. ` +
        `Refresh this page and click Sign in again. If it keeps happening, ` +
        `clear the site’s storage in your browser’s site settings.`
      );
    }
    if (raw.includes('did not complete within')) {
      // F16.J: our own 90s timeout fired. The popup either was blocked
      // or hung on a Microsoft error page we can't see from the parent.
      return raw;
    }
    if (raw.includes('timed_out') || raw.toLowerCase().includes('timed out')) {
      // F16.J: MSAL's own poll-loop timeout (aka.ms/msal.js.errors#timed_out).
      // Usually means the popup never redirected back to our origin —
      // most often a redirect-URI mismatch in the app reg, OR the user
      // dismissed the popup before completing sign-in.
      return (
        `Microsoft sign-in took too long to complete. This usually means ` +
        `the popup was closed before sign-in finished, or your firm's ` +
        `IT hasn't whitelisted this app yet. Close any leftover popups, ` +
        `refresh, and try again.`
      );
    }
    if (raw.includes('endpoints_resolution_error')) {
      return (
        `Microsoft couldn't return tenant configuration for tenant ` +
        `${tenantId}. Check that the tenant ID is correct and that ` +
        `your network can reach login.microsoftonline.com.`
      );
    }
    return raw;
  }

  async function handleSignOut() {
    if (!msal) {return;}
    setBusy(true);
    try {
      await msal.signOut();
      // Land on the branded signed-out screen so the portal identity persists.
      void navigate({ to: '/c/$clientSlug/signed-out', params: { clientSlug: slug } });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid={testIds.portal.root}
      data-portal-theme={branding.theme}
      data-portal-practice-area={branding.practiceArea}
      className="min-h-screen flex flex-col bg-white text-black font-sans"
      style={
        {
          // F16.B+ themes will override these via @layer components. Setting
          // them here gives F16.B chrome a sane fallback before the theme
          // CSS bundle loads.
          '--portal-accent': branding.primaryColor,
        } as React.CSSProperties
      }
    >
      <header
        data-testid={testIds.portal.titleBar}
        className="flex items-center gap-3 px-4 py-2 border-b-2 bg-white"
        // F16.G.2: high z-index so the title bar floats over the Win95
        // `position: fixed` desktop that the /c/$slug/desktop route
        // mounts beneath this header.
        style={{
          borderBottomColor: 'var(--portal-accent, currentColor)',
          position: 'relative',
          zIndex: 10000,
        }}
      >
        <TenantLogo size="titleBar" />
        <h1
          data-testid={testIds.portal.titleBarDisplayName}
          className="text-base font-semibold tracking-tight"
        >
          {branding.displayName}
        </h1>
        {branding.theme === 'art-deco' ? (
          <ArtDecoOrnament className="ml-1 opacity-80" />
        ) : null}
        {/* F16.D practice-area badge. Scopes the visible matter universe
            inside the portal; the reviewer + Active Client picker reuse
            this signal to filter rows. */}
        <span
          data-testid={testIds.portal.practiceAreaBadge}
          className="ml-2 inline-block text-xs uppercase tracking-wide font-semibold border px-2 py-0.5"
          style={{
            borderColor: 'var(--portal-accent, currentColor)',
            color: 'var(--portal-accent, currentColor)',
          }}
        >
          {branding.practiceArea === 'corporate-ma'
            ? 'Corporate / M&A'
            : 'Litigation / Disputes'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {/* SharePoint connection pill. Renders only when this portal is
              locked to one or more real SharePoint sites (lockedSiteIds
              populated by F16.H.2 / provisionDemoFirm.ts). Green when an
              MSAL account is active, grey otherwise. The pill exposes the
              actual site host + GUID prefix so a lawyer can verify in real
              time that data is coming from their tenant. */}
          {branding.lockedSiteIds.length > 0 ? (
            <SharePointConnectionPill
              lockedSiteIds={branding.lockedSiteIds}
              signedIn={account !== null}
              tenantDomain={branding.lockedTenantId}
            />
          ) : null}
          <button
            type="button"
            data-testid={testIds.portal.whatIsThis}
            onClick={() => setExplainOpen((v) => !v)}
            className="text-sm px-2 py-1 border border-black hover:bg-black hover:text-white transition-colors"
            aria-expanded={explainOpen}
            aria-controls="portal-what-is-this-panel"
          >
            What is this?
          </button>
          {account === null ? (
            <button
              type="button"
              data-testid={testIds.portal.signInButton}
              onClick={handleSignIn}
              disabled={busy || !accountReady}
              className="text-sm px-2 py-1 border border-black hover:bg-black hover:text-white transition-colors disabled:opacity-50"
            >
              {busy ? 'Signing in…' : 'Sign in with Microsoft'}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span
                data-testid={testIds.portal.accountChip}
                className="text-xs px-2 py-1 border border-black bg-black/[0.04]"
                title={accountLabel ?? ''}
              >
                {accountLabel}
              </span>
              <button
                type="button"
                data-testid={testIds.portal.signOutButton}
                onClick={handleSignOut}
                disabled={busy}
                className="text-sm px-2 py-1 border border-black hover:bg-black hover:text-white transition-colors disabled:opacity-50"
              >
                {busy ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          )}
        </div>
      </header>
      {signInError ? (
        <div
          role="alert"
          data-testid={testIds.portal.signInError}
          className="px-4 py-2 border-b border-red-700 bg-red-50 text-sm text-red-900"
        >
          {signInError}
        </div>
      ) : null}

      {explainOpen ? (
        <div
          id="portal-what-is-this-panel"
          data-testid={testIds.portal.whatIsThisPanel}
          role="region"
          aria-label="What is this portal?"
          className="px-4 py-3 border-b border-black/20 bg-black/[0.04] text-sm leading-relaxed"
        >
          <p className="mb-1 font-semibold">
            {branding.displayName} is a Repancy portal.
          </p>
          <p>
            Repancy is an AI-first legal workspace. This portal is branded for
            {' '}
            <strong>{branding.displayName}</strong>, scoped to that firm&rsquo;s
            Microsoft 365 tenant, and pre-configured with a demo workspace so
            you can try the AI tools before connecting your own SharePoint.
          </p>
        </div>
      ) : null}

      <main className="flex-1">{children}</main>
    </div>
  );
}

interface SharePointConnectionPillProps {
  lockedSiteIds: readonly string[];
  signedIn: boolean;
  tenantDomain: string;
}

function SharePointConnectionPill({
  lockedSiteIds,
  signedIn,
  tenantDomain,
}: SharePointConnectionPillProps) {
  // Graph site ids are `hostname,siteGuid,webGuid` — surface the hostname
  // so a lawyer sees something recognizable (e.g. areweai.sharepoint.com)
  // rather than an opaque GUID.
  const first = lockedSiteIds[0] ?? '';
  const host = first.split(',')[0] ?? '';
  const remaining = lockedSiteIds.length - 1;
  const tooltip =
    `Locked to ${lockedSiteIds.length} SharePoint site${lockedSiteIds.length === 1 ? '' : 's'} ` +
    `under tenant ${tenantDomain.slice(0, 8)}…\n` +
    lockedSiteIds.map((s) => `• ${s.split(',')[0]}`).join('\n');
  return (
    <span
      data-testid={testIds.spProvenance.chromePill}
      title={tooltip}
      className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border ${
        signedIn
          ? 'border-green-700 bg-green-50 text-green-900'
          : 'border-gray-500 bg-gray-50 text-gray-700'
      }`}
    >
      <span
        className={`inline-block w-2 h-2 rounded-full ${
          signedIn ? 'bg-green-600' : 'bg-gray-400'
        }`}
        aria-hidden="true"
      />
      <span className="font-semibold">SharePoint</span>
      <span
        data-testid={testIds.spProvenance.chromePillSiteUrl}
        className="font-mono opacity-80"
      >
        {host}
        {remaining > 0 ? ` +${remaining}` : ''}
      </span>
    </span>
  );
}
